import { useIsMobile } from '@hts/shared/libs';
import { CardService } from '@hts/shared/services';
import { HtslIcon, IconInput } from '@hts/shared/ui';
import { memo, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styles from '../../styles/SearchForm/SearchForm.module.scss';
import { ResultsPopup } from './ResultsPopup';
import { ENotAuthNavigation } from '@hts/shared/enums';

interface ISearchFormProps {
  onClose: (redirectTo?: string) => void;
}

export const SearchForm: React.FC<ISearchFormProps> = memo(({ onClose }) => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  const [searchValue, setSearchValue] = useState('');
  const [isResultsActive, setResultsActive] = useState(false);

  const cardService = useMemo(() => new CardService(), []);

  const handleClose = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
      }

      setResultsActive(false);
      onClose();
    },
    [onClose]
  );

  const getSearchResults = useCallback(
    async (search: string) => {
      const res = await cardService.getSearchResults(search.trim());

      const { model, type } = res;

      let redirect: string | undefined;

      switch (type) {
        case 'card':
          redirect = `${ENotAuthNavigation.CARD}/${model.id}`;
          break;

        case 'category':
          if (model.parent_id && model.parent) {
            redirect = `${ENotAuthNavigation.CATEGORY_OF_CARD}/${model.parent.slug}?sub=${model.slug}`;
          } else {
            redirect = `${ENotAuthNavigation.CATEGORY_OF_CARD}/${model.slug}`;
          }

          break;

        default:
          break;
      }

      if (pathname === redirect) {
        redirect = undefined;
      }

      setSearchValue('');
      setResultsActive(type === 'empty');
      onClose(redirect);
    },
    [cardService, pathname, onClose]
  );

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (searchValue) {
        getSearchResults(searchValue);
      }
    },
    [getSearchResults, searchValue]
  );

  return (
    <>
      <form className={styles.container} onSubmit={onSubmit}>
        <IconInput
          value={searchValue}
          leftIcon={<HtslIcon icon="search" />}
          fullWidth
          containerProps={{ className: !isMobile ? styles.desktopSearchInputContainer : '' }}
          inputProps={{ className: styles.searchInput }}
          onChangeValue={setSearchValue}
        />
      </form>

      {isResultsActive && <ResultsPopup closePopup={handleClose} />}
    </>
  );
});
